import React, {useState} from 'react';
import { mergeAll } from "ramda";
import { object, string, mixed, number, reach, boolean } from "yup";
import { throwError } from 'rxjs';
import { prepopulatedRetirementPlannerForm } from '../../constants/constants';

const usePlanRetirementForm = (callback) => {
  const blankScenario = {
    value: '',
    status: 'neutral',
    error: '',
    validation: number("No puede estar vacío").transform(value => (isNaN(value) ? undefined : value)).positive("Debe ser mayor que 0").required("Todos los escenarios son campos requeridos"),
  };

  const [state, setState] = useState({
    errors: [],
    submitting: '',
    message: '',
  })

  let initialState;
  if (process.env.NODE_ENV === 'production') {
    initialState = {
      file: {
        value: '',
        status: 'neutral',
        error: '',
        validation: mixed().test('fileType', "Debes adjuntar un documento que tenga la extensión .pdf", value => value && value.includes('.pdf'))
      },
      user_email: {
        value: '',
        status: 'neutral',
        error: '',
        validation: string().required("El correo del cliente es un campo requerido").email("Debe ser un correo electrónico válido"),
      },
      advisor_email: {
        value: '',
        status: 'neutral',
        error: '',
        validation: string().required("El correo del asesor es un campo requerido").email("Debe ser un correo electrónico válido"),
      },
      underage_children_num: {
        value: '',
        status: 'neutral',
        error: '',
        validation: number().min(0,"La cantidad de hij@s menores de edad en el retiro debe ser mayor que o igual a 0").required("La cantidad de hij@s mejores de edad en el retiro es requerida").transform(value => (isNaN(value) ? undefined : value)),
      },
      civil_status: {
        value: '',
        status: 'neutral',
        error: '',
        validation: string().required("El estado civil es un campo requerido"),
      },
      alive_parents_num: {
        value: '',
        status: 'neutral',
        error: '',
        validation: number().min(0, "La cantidad de padres vivos debe ser mayor que o igual a 0").transform(value => (isNaN(value) ? undefined : value)),
      },
      extra_weeks: {
        value: '',
        status: 'neutral',
        error: '',
        validation: number().positive("La cantidad de semanas extra debe ser mayor que 0").transform(value => (isNaN(value) ? undefined : value)),
      },
      reactivation_wage: {
        value: '',
        status: 'neutral',
        error: '',
        validation: number().positive("El salario de reactivación debe ser mayor que 0").transform(value => (isNaN(value) ? undefined : value)),
      },
      edge_case: {
        value: false,
        status: 'neutral',
        error: '',
        validation: boolean(),
      },
      mod_40_scenarios: [blankScenario],
    };
  } else {
    initialState = prepopulatedRetirementPlannerForm;
  }

  const [inputs, setInputs] = useState(initialState);

  const schema = object().shape(
    mergeAll(
      Object.keys(inputs).map(key => {
        if (key != 'mod_40_scenarios') {
          return {
            [key]: inputs[key].validation
          };
        }
      })
    )
  );

  const generateGlobalErrors = (array) => {
    let errors = [];
    let promises = []
    for (let i = 0; i<array.length; i++) {
      if (array[i] == "mod_40_scenarios") {
        for(let j = 0; j < inputs[array[i]].length; j++) {
          promises.push(inputs[array[i]][j].validation.validate(inputs[array[i]][j].value).then(()=> {
            return null;
          }).catch(err => {
            return err.errors;
          }))
        }
      } else {
        promises.push(inputs[array[i]].validation.validate(inputs[array[i]].value).then(()=> {
          return null;
        }).catch(err => {
          return err.errors;
        }))
      }
    }
    
    return Promise.all(promises).then(function(values) {
      values.filter(function (el) {
        return el != null;
      }).map(el => {
        errors.push(...el);
      });
      return errors;
    });
  }

  const isEntireFormValid = () => {
    let array = [
      "file",
      "user_email",
      "advisor_email",
      "underage_children_num",
      "civil_status",
      "alive_parents_num",
      "extra_weeks",
      "reactivation_wage",
      "mod_40_scenarios"
    ];

    return generateGlobalErrors(array)
  }

  const handleSubmit = (event) => {
    if (event) {
      event.preventDefault();
    }

    isEntireFormValid().then(res => {
      if (res.length >= 1) {
        setState(state => ({...state, errors: res}));
      } else {
        let submittingMessage = "Creando plan de pensión... (Este paso puede tardar hasta 1 minuto)";
        if (inputs.edge_case.value) {
          submittingMessage = "Enviando a Finauta para revisión..."
        }
        setState(state => ({...state, errors: [], message: '', submitting: submittingMessage}));
        setInputs(inputs => ({...inputs, }));
        callback((result) => {
          if (typeof(result) != "string") {
            let array = []
            if (typeof(result) == "object") {
              if (!'response' in result) {
                for (let key in result) {
                  if (Array.isArray(result[key])) {
                    for (let i = 0; i<result[key].length; i++) {
                      array.push(`${key} : ${result[key][i]}`);
                    }
                  } else {
                    array.push(result[key])
                  }
                }
              } else {
                for (let key in result.response.data) {
                  if (Array.isArray(result.response.data[key])) {
                    for (let i = 0; i<result.response.data[key].length; i++) {
                      array.push(`${key} : ${result.response.data[key][i]}`);
                    }
                  } else {
                    array.push(result.response.data[key])
                  }
                }
              }
              setState(state => ({...state, message: '', errors: array, submitting: ''}));
            } else if (result.response.headers['content-type'] == "application/json") {
              let responseData = result.response.data;
              for (let key in responseData) {
                if (Array.isArray(responseData[key])) {
                  for (let i = 0; i<responseData[key].length; i++) {
                    array.push(`${key} : ${responseData[key][i]}`);
                  }
                } else {
                  array.push(responseData[key])
                }
              }
              setState(state => ({...state, message: '', errors: array, submitting: ''}));
            } else {
              setState(state => ({...state, message: '', errors: ["Hubo un error en el sistema, favor de habilitar 'Enviar a Finauta para revisión' para que podamos arreglarlo a la brevedad",], submitting: ''}));
            }
          } else {
            if (result.includes('500')) {
              setState(state => ({...state, message: '', errors: ["Hubo un error en el sistema, favor de habilitar 'Enviar a Finauta para revisión' para que podamos arreglarlo a la brevedad",], submitting: ''}));
            } else {
              setState(state => ({...state, submitting: '', message: result}));
            }
          }
        });
      }
    }).catch(err => {
      throw new Error();
    })
  }

  const handleInputChange = (event) => {
    event.persist();
    let valueToValidate = (event.target.type != "checkbox") ? event.target.value : event.target.checked;
    reach(schema, event.target.name).validate(valueToValidate).then(() => {
      if (event.target.value == '') {
        setInputs(inputs => ({...inputs, [event.target.name]: { ...inputs[event.target.name], status: 'neutral', error: ""}}));    
      } else {
        setInputs(inputs => ({...inputs, [event.target.name]: { ...inputs[event.target.name], status: 'succeeding', error: ""}}));    
      }
    }).catch(error => {
      setInputs(inputs => ({...inputs, [event.target.name]: { ...inputs[event.target.name], status: 'failing', error: error.message}}));    
    });
    if (event.target.type != "checkbox") {
      setInputs(inputs => ({...inputs, [event.target.name]: { ...inputs[event.target.name], value: event.target.value, error: ""}}));  
    } else {
      setInputs(inputs => ({...inputs, [event.target.name]: { ...inputs[event.target.name], value: event.target.checked, error: ""}}));
    }
    
  }

  const addScenario = () => {
    setInputs(inputs => ({...inputs, mod_40_scenarios: [...inputs.mod_40_scenarios, blankScenario]}));
  };

  const removeScenario = () => {
    let scenarios = [...inputs.mod_40_scenarios];
    if (scenarios.length > 1) {
      scenarios.pop();
      setInputs(inputs => ({...inputs, mod_40_scenarios: scenarios}));
    }
  };

  const handleScenarioChange = (e) => {
    const updatedScenarios = [...inputs.mod_40_scenarios];
    const index = e.target.dataset.idx;
    const className = e.target.className;
    const value = e.target.value;
    updatedScenarios[index][className] = value;
    inputs.mod_40_scenarios[e.target.dataset.idx].validation.validate(e.target.value).then(val => {
      updatedScenarios[index]['error'] = '';
      if (val == '') {
        updatedScenarios[index]['status'] = 'neutral';
        setInputs(inputs => ({...inputs, mod_40_scenarios: updatedScenarios}));
      } else {
        updatedScenarios[index]['status'] = 'succeeding';
        setInputs(inputs => ({...inputs, mod_40_scenarios: updatedScenarios}));
      }
    }).catch(error => {
      updatedScenarios[index]['status'] = 'failing';
      updatedScenarios[index]['error'] = error.message;
      setInputs(inputs => ({...inputs, mod_40_scenarios: updatedScenarios}));
    });
    
  };

  return {
    handleSubmit,
    handleInputChange,
    addScenario,
    removeScenario,
    handleScenarioChange,
    inputs,
    state,
  };
}
export default usePlanRetirementForm;